import { useEffect, useRef, useState } from "react";
import { getCookie, setCookie } from "../../lib/cookies";
import { cn } from "../../utils/cn";
import { supportedLanguages } from "../../i18n/supportedLanguages";
import { Icon } from "../ui/Icon";
import { Badge } from "../ui/Badge";
import { MenuPortal } from "./MenuPortal";
import { useMenuPosition } from "./useMenuPosition";

export const LanguageMenu = () => {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(() => {
    const stored = getCookie("qb_lang") ?? "";
    return supportedLanguages.some((item) => item.code === stored)
      ? stored
      : supportedLanguages[0].code;
  });
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuStyle = useMenuPosition(anchorRef, open, { align: "right" });

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const currentLanguage =
    supportedLanguages.find((item) => item.code === language) ?? supportedLanguages[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        ref={anchorRef}
        className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/70 px-1.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:px-3 sm:py-2"
      >
        <Icon icon="lucide:globe" className="h-4 w-4" />
        <span className="hidden text-xs text-muted-foreground sm:inline">{currentLanguage.name}</span>
      </button>

      {open && (
        <MenuPortal>
          <div
            ref={menuRef}
            style={menuStyle}
            className="relative z-[100] w-52 max-w-[calc(100vw-1.5rem)] rounded-lg border border-border/50 bg-card/95 p-2 shadow-soft"
          >
            {/* Coming Soon overlay */}
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-[1px]">
              <Badge variant="warning" size="md">
                <Icon icon="lucide:construction" className="h-3.5 w-3.5" />
                Coming Soon
              </Badge>
            </div>

            {/* Language options (dimmed + non-interactive) */}
            <div className="opacity-40 pointer-events-none select-none">
              {supportedLanguages.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => {
                    setLanguage(item.code);
                    setCookie("qb_lang", item.code, 365);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs transition-colors",
                    language === item.code
                      ? "bg-muted/70 text-foreground"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  )}
                >
                  <img src={item.flag} alt={`${item.code} flag`} className="h-4 w-4 rounded-sm" />
                  <span className="flex-1 text-left">{item.name}</span>
                  {language === item.code && <Icon icon="lucide:check" className="h-3 w-3" />}
                </button>
              ))}
            </div>
          </div>
        </MenuPortal>
      )}
    </div>
  );
};
